/**
 * CollectForm V2 Schema
 *
 * This is the new, advanced schema for collect forms supporting:
 * - Conditional rendering
 * - Advanced validation
 * - Computed fields
 * - Repeatable field groups
 * - Dynamic options
 * - Multi-step forms
 */

import { Syncable } from './Syncable';

// ============================================
// Field Types
// ============================================

export enum FieldType_V2 {
  // Basic
  TEXT = 'text',
  TEXTAREA = 'textarea',
  NUMBER = 'number',

  // Date/Time
  DATE = 'date',
  TIME = 'time',
  DATETIME = 'datetime',

  // Selection
  SELECT = 'select',
  RADIO = 'radio',
  CHECKBOX = 'checkbox',
  MULTISELECT = 'multiselect',

  // Advanced
  COMPUTED = 'computed',
  CASCADE_SELECT = 'cascadeSelect',
  REPEATER = 'repeater',
  FILE = 'file',

  // Special
  HEADER = 'header',
  DIVIDER = 'divider',
  HTML = 'html',
}

// ============================================
// Conditional Logic
// ============================================

export type ConditionalRule = {
  type: 'simple' | 'expression';

  // Simple: single condition
  simple?: {
    field: string;
    operator:
      | '=='
      | '!='
      | '>'
      | '<'
      | '>='
      | '<='
      | 'contains'
      | 'startsWith'
      | 'endsWith'
      | 'in'
      | 'notIn';
    value: any;
  };

  // Expression: complex logic (future)
  // e.g., "(field1 == 'A' && field2 > 100) || field3"
  expression?: string;
};

// ============================================
// Validation Rules
// ============================================

export type FieldValidation = {
  // Basic
  required?: boolean;
  unique?: boolean;

  // String
  pattern?: string; // Regex
  minLength?: number;
  maxLength?: number;

  // Number
  min?: number;
  max?: number;
  integer?: boolean;

  // Array (for multi-select, repeater)
  minItems?: number;
  maxItems?: number;

  // Custom validator function name
  customValidator?: string;

  // Cross-field validation
  dependentValidation?: {
    field: string;
    rule: string; // e.g., "this >= field", "this != field"
    message: string;
  };

  // Error messages
  messages?: {
    required?: string;
    pattern?: string;
    min?: string;
    max?: string;
    minLength?: string;
    maxLength?: string;
    custom?: string;
  };
};

// ============================================
// Field Configurations
// ============================================

export type TextFieldConfig = {
  multiline?: boolean;
  numberOfLines?: number;
  keyboardType?: 'default' | 'email-address' | 'numeric' | 'phone-pad';
  autoCapitalize?: 'none' | 'sentences' | 'words' | 'characters';
};

export type NumberFieldConfig = {
  step?: number;
  format?: string; // e.g., "0,0.00" for thousand separator
  prefix?: string; // e.g., "$", "VNĐ"
  suffix?: string;
};

export type SelectOption = {
  value: any;
  label: string;
  icon?: string;
  disabled?: boolean;
  metadata?: Record<string, any>;
};

/**
 * SQLite query configuration for dynamic options
 */
export type SqliteFieldConfig = {
  database: string; // SQLite filename from form's sqlite_files
  table: string; // Table name to query
  valueColumn: string; // Column for option value
  labelColumn: string; // Column for option label
  where?: string; // Optional WHERE clause (e.g., "province_code = '${province}'")
  orderBy?: string; // Optional ORDER BY clause (e.g., "district_name ASC")
};

export type SelectFieldConfig = {
  options?: SelectOption[]; // Static options
  sqlite?: SqliteFieldConfig; // Dynamic options from SQLite query
  optionsSource?: {
    type: 'static' | 'api' | 'database' | 'cascade';
    endpoint?: string;
    query?: string;
    valueField?: string;
    labelField?: string;
    filters?: Record<string, any>;
  };
  multiple?: boolean;
  searchable?: boolean;
  allowCustom?: boolean;
};

export type ComputedFieldConfig = {
  expression: string; // e.g., "field1 * field2 + 100"
  format?: string; // e.g., "0,0.00"
  readonly: true;
  dependencies: string[]; // List of field IDs this computed field depends on
};

export type RepeaterFieldConfig = {
  fields: FormField_V2[];
  minItems?: number;
  maxItems?: number;
  addButtonLabel?: string;
  removeButtonLabel?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
};

export type FileFieldConfig = {
  accept?: string[]; // ['image/*', '.pdf', 'video/*']
  maxSize?: number; // bytes
  minFiles?: number;
  maxFiles?: number;
  capture?: 'camera' | 'gallery' | 'both';
  multiple?: boolean;
};

export type CascadeSelectConfig = {
  sourceField: string; // The field that triggers cascade
  optionsMap?: Record<string, SelectOption[]>; // Static mapping
  optionsSource?: {
    type: 'api' | 'database';
    endpoint?: string;
    query?: string; // SQL query template
    parentParam?: string; // Parameter name for parent value
  };
};

// Union type for all field configs
export type FieldConfig =
  | TextFieldConfig
  | NumberFieldConfig
  | SelectFieldConfig
  | ComputedFieldConfig
  | RepeaterFieldConfig
  | FileFieldConfig
  | CascadeSelectConfig;

// ============================================
// Field Definition
// ============================================

export type FormField_V2 = {
  id: string;
  label: string;
  type: FieldType_V2;

  // Layout & Grouping
  section?: string; // Section ID this field belongs to
  order?: number;
  width?: 'full' | 'half' | 'third' | 'quarter';

  // Conditional Behavior
  required?: boolean | ConditionalRule;
  readonly?: boolean | ConditionalRule;
  hidden?: boolean | ConditionalRule;
  disabled?: boolean | ConditionalRule;

  // Validation
  validation?: FieldValidation;

  // Field-specific configuration
  config?: FieldConfig;

  // Dependencies
  dependsOn?: string[]; // Fields this field depends on
  affects?: string[]; // Fields affected by this field

  // UI/UX
  helpText?: string;
  placeholder?: string;
  defaultValue?: any;
  icon?: string;

  // Metadata
  metadata?: Record<string, any>;
};

// ============================================
// Section & Layout
// ============================================

export type FormSection = {
  id: string;
  title: string;
  description?: string;
  fields: string[]; // Field IDs
  collapsible?: boolean;
  defaultExpanded?: boolean;
  conditional?: ConditionalRule;
  order?: number;
  icon?: string;
};

// ============================================
// Multi-step Forms
// ============================================

export type FormStep = {
  id: string;
  title: string;
  description?: string;
  sections: string[]; // Section IDs
  validation?: 'onNext' | 'onSubmit' | 'none';
  order?: number;
};

// ============================================
// Form Settings
// ============================================

export type FormSettings = {
  submitButtonLabel?: string;
  cancelButtonLabel?: string;

  // Multi-step
  multiStep?: {
    enabled: boolean;
    steps: FormStep[];
    showProgress?: boolean;
    allowBackNavigation?: boolean;
  };

  // Auto-save
  autoSave?: boolean;
  autoSaveInterval?: number; // milliseconds

  // Confirmation
  confirmBeforeSubmit?: boolean;
  confirmMessage?: string;

  // Styling
  theme?: 'default' | 'compact' | 'modern';
  accentColor?: string;
};

// ============================================
// Main Schema V2
// ============================================

export type FormSchema_V2 = {
  sections?: FormSection[];
  fields: FormField_V2[];
  validation?: {
    mode?: 'onSubmit' | 'onBlur' | 'onChange';
    revalidateMode?: 'onSubmit' | 'onBlur' | 'onChange';
  };
  settings?: FormSettings;
  metadata?: {
    version: string;
    createdWith?: string; // App version
    tags?: string[];
  };
};

// ============================================
// CollectForm V2 Types
// ============================================

export type CollectForm_V2 = Syncable & {
  uuid: string;
  name: string;
  description?: string;
  form_version: 2; // Always 2 for V2 forms
  content: FormSchema_V2; // JSON schema stored in content column
  project_id: number | null;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  user_id?: number;
  sync_at?: string;
  prev_version: string;
  sqlite_files?: string; // Comma-separated list of SQLite file names for querying
};

// ============================================
// Utility Types
// ============================================

/**
 * Represents form data with field values
 */
export type FormData_V2 = Record<string, any>;

/**
 * Field error type
 */
export type FieldError = {
  type: string;
  message: string;
};

/**
 * Form validation result
 */
export type FormValidationResult = {
  isValid: boolean;
  errors: Record<string, FieldError>;
};
